import { Box, CircularProgress } from '@mui/material';
import { JSX, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useBranding } from '../../../contexts/BrandingContext';
import { bookAppointmentLink, landingLinkFor } from '../../../library/constants';
import { readDraft } from './bookingDraft';
import { pendingBoxStyles } from './publicBooking.styles';
import { PublicBookingWizard } from './PublicBookingWizard';
import { VerifyBooking } from './VerifyBooking';

/**
 * Online booking: a visitor chooses a service, a therapist and a time on a clinic's own
 * website, and says who they are only at the end.
 *
 * **Unlisted on purpose.** Nothing in the app links here -- not the front page, not the
 * header, not the price list. The flow is reached only by somebody who has been given the
 * address, and `LandingPage.test` fails if the front page ever grows a link to it.
 *
 * **Self-contained on purpose.** Everything the feature draws lives in this folder, and the
 * rest of the app knows about it in three places: the `/book/*` route in `App.tsx`, the
 * `publicBooking` entry in `services.ts`, and the `public_booking_*` keys in both locales.
 * Removing it is deleting this folder, `services/publicBookingService.ts` and
 * `library/models/publicBooking.ts`, and those three.
 *
 * Only on a clinic's own address: the platform's names no clinic, so there is nothing to book
 * and the API answers every route with a 404 -- the page goes home instead of drawing a form
 * that can only fail.
 */
export const PublicBooking = (): JSX.Element => {
    const { hostContext, isHostPending, isPlatformHost } = useBranding();

    if (isHostPending) {
        return (
            <Box sx={pendingBoxStyles}>
                <CircularProgress />
            </Box>
        );
    }

    if (isPlatformHost || hostContext === null) {
        return <Navigate to="/home" replace />;
    }

    return (
        <Routes>
            <Route index element={<ChooseGate />} />
            <Route path="verify" element={<VerifyBooking />} />
        </Routes>
    );
};

/**
 * Who the choosing steps are for.
 *
 * A signed-out visitor, always. A signed-in patient only when they are half-way through --
 * they signed in at the last step, or came back from a link whose time had been taken -- and
 * otherwise the booking page every patient already has. Staff are never booked in as patients.
 */
const ChooseGate = (): JSX.Element => {
    const { isLoading } = useAuth();

    if (isLoading) {
        return (
            <Box sx={pendingBoxStyles}>
                <CircularProgress />
            </Box>
        );
    }

    return <ChooseDecision />;
};

const ChooseDecision = (): JSX.Element => {
    const { isAuthenticated, role } = useAuth();
    const { hostname } = useBranding();

    /*
     * Decided once, on arrival. Asked again on every render it would change its mind the moment
     * the wizard finishes -- the booking clears the draft, the snackbar re-renders the tree, and
     * this would bounce a patient who has just booked to the booking page instead of their list.
     */
    const [nothingInProgress] = useState<boolean>(() => isAuthenticated && readDraft(hostname) === null);

    if (isAuthenticated && role !== null && role !== 'CUSTOMER') {
        return <Navigate to={landingLinkFor(role)} replace />;
    }

    if (nothingInProgress) {
        return <Navigate to={bookAppointmentLink} replace />;
    }

    return <PublicBookingWizard />;
};
